import { useCallback, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader, ErrorState, StatusPill } from '@ui-kit';
import { useMe } from '../../_shared/hooks/useMe';
import { useStandardTerm } from '../hooks/useStandardTerm';
import { useUpsertStandardTerm } from '../hooks/useUpsertStandardTerm';
import { useDeleteStandardTerm } from '../hooks/useDeleteStandardTerm';
import { StandardTermFormDialog } from '../components/StandardTermFormDialog';
import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog';
import { TerminologyToast, useToast } from '../components/TerminologyToast';
import type { ApiError } from '../types/terminology';
import type { StandardTermFormValues } from '../schema/terminologySchema';

/* ─── Detail field row ──────────────────────────────────────────────────── */
function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        padding: 'var(--spacing-3) 0',
        borderBottom: '1px solid var(--color-surface-border)',
        gap: 'var(--spacing-4)',
        flexWrap: 'wrap',
      }}
    >
      <dt
        style={{
          width: 160,
          flexShrink: 0,
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          color: 'var(--color-text-secondary)',
        }}
      >
        {label}
      </dt>
      <dd
        style={{
          flex: 1,
          margin: 0,
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-primary)',
          wordBreak: 'break-all',
        }}
      >
        {value ?? '-'}
      </dd>
    </div>
  );
}

/* ─── Skeleton ──────────────────────────────────────────────────────────── */
function DetailSkeleton() {
  return (
    <div aria-busy="true">
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          aria-hidden="true"
          style={{
            height: 48,
            margin: 'var(--spacing-1) 0',
            background: 'var(--color-surface-muted)',
            borderRadius: 'var(--radius-sm)',
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
      ))}
    </div>
  );
}

/* ─── Main Page ─────────────────────────────────────────────────────────── */

export function StandardTermDetailPage() {
  const { id } = useParams<{ id: string }>();
  const termId = id ? parseInt(id, 10) : undefined;
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const meQuery = useMe();
  const isAdmin = meQuery.data?.role === 'ROLE_ADMIN';

  const termQuery = useStandardTerm(termId);
  const upsertMutation = useUpsertStandardTerm();
  const deleteMutation = useDeleteStandardTerm();
  const { messages, addToast, dismissToast } = useToast();

  const [formOpen, setFormOpen] = useState(false);
  const [formError, setFormError] = useState<string | undefined>();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const handleFormSubmit = useCallback(
    (values: StandardTermFormValues) => {
      if (!termId) return;
      setFormError(undefined);
      upsertMutation.mutate(
        {
          id: termId,
          body: {
            korean: values.korean,
            englishShort: values.englishShort,
            englishFull: values.englishFull || undefined,
            description: values.description || undefined,
            domain: values.domain || undefined,
            source: values.source || undefined,
            status: values.status,
          },
        },
        {
          onSuccess: () => {
            setFormOpen(false);
            setFormError(undefined);
            addToast(t('terminologyManagement.toast.updated'));
          },
          onError: (err: unknown) => {
            const apiErr = err as ApiError;
            const msgKey = `terminologyManagement.error.${apiErr.code}`;
            const translated = t(msgKey);
            setFormError(translated !== msgKey ? translated : apiErr.message);
          },
        }
      );
    },
    [termId, upsertMutation, addToast, t]
  );

  const handleDeprecate = useCallback(() => {
    if (!termId) return;
    deleteMutation.mutate(
      { id: termId },
      {
        onSuccess: () => {
          setDeleteOpen(false);
          addToast(t('terminologyManagement.toast.deprecated'));
          navigate('/admin/terminology/standard-terms');
        },
        onError: (err: unknown) => {
          setDeleteOpen(false);
          addToast((err as ApiError)?.message ?? t('terminologyManagement.error.network'), 'error');
        },
      }
    );
  }, [termId, deleteMutation, addToast, navigate, t]);

  const formatDate = (iso?: string | null) => {
    if (!iso) return '-';
    return new Intl.DateTimeFormat(i18n.language === 'en' ? 'en-US' : 'ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  };

  const term = termQuery.data;

  return (
    <div style={{ padding: 'var(--spacing-6)', maxWidth: 900, margin: '0 auto' }}>
      <PageHeader
        title={term ? term.korean : t('terminologyManagement.detail.field.korean')}
        breadcrumb={<span>{t('terminologyManagement.term.list.title')}</span>}
        actions={
          <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
            <button
              type="button"
              onClick={() => navigate('/admin/terminology/standard-terms')}
              style={{
                padding: 'var(--spacing-2) var(--spacing-3)',
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
                color: 'var(--color-text-secondary)',
              }}
            >
              ← {t('terminologyManagement.detail.button.back')}
            </button>
            {isAdmin && term && (
              <>
                <button
                  type="button"
                  data-testid="term-detail-edit-btn"
                  onClick={() => setFormOpen(true)}
                  style={{
                    padding: 'var(--spacing-2) var(--spacing-4)',
                    background: 'var(--color-brand-primary)',
                    color: 'var(--color-text-inverse)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family-sans)',
                  }}
                >
                  {t('terminologyManagement.detail.button.edit')}
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteOpen(true)}
                  style={{
                    padding: 'var(--spacing-2) var(--spacing-4)',
                    background: 'var(--color-danger)',
                    color: 'var(--color-text-inverse)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family-sans)',
                  }}
                >
                  {t('terminologyManagement.detail.button.deprecate')}
                </button>
              </>
            )}
          </div>
        }
      />

      {/* Loading state */}
      {termQuery.isLoading && <DetailSkeleton />}

      {/* Error state */}
      {termQuery.isError && (
        <ErrorState
          title={t('terminologyManagement.error.TERM-404-001')}
          message={(termQuery.error as unknown as ApiError)?.message ?? t('terminologyManagement.error.network')}
          onRetry={() => void termQuery.refetch()}
          actions={[
            {
              label: t('terminologyManagement.detail.button.back'),
              onClick: () => navigate('/admin/terminology/standard-terms'),
              variant: 'secondary',
            },
          ]}
        />
      )}

      {/* Default: detail view */}
      {term && !termQuery.isError && (
        <dl style={{ margin: 0 }}>
          <DetailRow label={t('terminologyManagement.detail.field.id')} value={term.id} />
          <DetailRow label={t('terminologyManagement.detail.field.korean')} value={<strong>{term.korean}</strong>} />
          <DetailRow
            label={t('terminologyManagement.detail.field.englishShort')}
            value={<span style={{ fontFamily: 'var(--font-family-mono)' }}>{term.englishShort}</span>}
          />
          <DetailRow label={t('terminologyManagement.detail.field.englishFull')} value={term.englishFull} />
          <DetailRow label={t('terminologyManagement.detail.field.description')} value={term.description} />
          <DetailRow label={t('terminologyManagement.detail.field.domain')} value={term.domain} />
          <DetailRow label={t('terminologyManagement.detail.field.source')} value={term.source} />
          <DetailRow
            label={t('terminologyManagement.detail.field.status')}
            value={
              <StatusPill
                tone={term.status === 'ACTIVE' ? 'success' : term.status === 'DEPRECATED' ? 'neutral' : 'warning'}
              >
                {t(`terminologyManagement.status.${term.status}`)}
              </StatusPill>
            }
          />
          <DetailRow
            label={t('terminologyManagement.detail.field.autoAdded')}
            value={
              term.autoAdded
                ? t('terminologyManagement.detail.autoAdded.yes')
                : t('terminologyManagement.detail.autoAdded.no')
            }
          />
          {term.aiTrace && (
            <DetailRow
              label={t('terminologyManagement.detail.field.aiTrace')}
              value={<span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>{term.aiTrace}</span>}
            />
          )}
          <DetailRow label={t('terminologyManagement.detail.field.createdAt')} value={formatDate(term.createdAt)} />
          <DetailRow label={t('terminologyManagement.detail.field.createdBy')} value={term.createdBy} />
          <DetailRow label={t('terminologyManagement.detail.field.updatedAt')} value={formatDate(term.updatedAt)} />
          <DetailRow label={t('terminologyManagement.detail.field.updatedBy')} value={term.updatedBy} />
        </dl>
      )}

      {/* Dialog state: edit */}
      <StandardTermFormDialog
        open={formOpen}
        editTarget={term ?? null}
        isLoading={upsertMutation.isPending}
        errorMessage={formError}
        onClose={() => { setFormOpen(false); setFormError(undefined); }}
        onSubmit={handleFormSubmit}
      />

      {/* Dialog state: deprecate confirm */}
      <DeleteConfirmDialog
        open={deleteOpen}
        name={term?.korean ?? ''}
        isLoading={deleteMutation.isPending}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeprecate}
      />

      <TerminologyToast messages={messages} onDismiss={dismissToast} />
    </div>
  );
}
